/**
 * One project, drawn as a passe-partout: ink mount, drawing inside, caption beneath, a meta
 * strip full-bleed across the foot. Geometry from `design/project/screens/projects-list.jsx`.
 *
 * **Everything on this card comes from `ProjectResponse`, and the mock's remaining furniture
 * is drawn empty rather than invented.** The prototype shows a pinned drawing, "7 reviews",
 * "8 overdue" and tags; the endpoint carries none of those, so the thumbnail is the
 * placeholder drawing and the counter row is a reserved, empty slot. A card that showed a
 * plausible zero would be a card nobody could tell apart from a real one
 * (KLQ-20260819-003, "no fake data").
 *
 * **There is no phase on this card, and that is a data fact rather than a design one**
 * (`KLQ-20260827-002`). The free-text `project.phase` this card used to print is gone; what
 * `ProjectResponse` carries now is `current_phase_id`, an id whose *label* lives in
 * `GET /projects/{id}/phases`. Naming it here would mean one phase request per card — twenty
 * four of them on a first page, for one word each — which is precisely the fan-out the
 * ecosystem data-fetching rule exists to stop. Printing a raw UUID, or a dot with nothing
 * beside it, would be worse than printing nothing. It comes back the day the list endpoint
 * carries the resolved label, and not before; the phase bar inside the project is where the
 * question is answered in the meantime.
 */

import { Link } from '@tanstack/react-router';

import { FloorPlanPattern } from '@/components';
import { relativeTime, useLocale, useMessages } from '@/lib/i18n';

import type { Project } from './api';
import styles from './ProjectsPage.module.css';

export function ProjectCard({ project }: { readonly project: Project }) {
  const t = useMessages('projects');
  const { locale } = useLocale();

  const shared = project.relation === 'shared';
  // Client and address are both optional; the separator only belongs between two of them.
  const caption = [project.client_name, project.address]
    .filter((part): part is string => part !== null && part !== undefined && part !== '')
    .join(t('card.separator'));

  return (
    <Link
      to="/projects/$projectId"
      params={{ projectId: project.id }}
      className={[styles.card, shared ? styles.cardShared : ''].filter(Boolean).join(' ')}
    >
      <div className={styles.thumb}>
        <FloorPlanPattern />
        {shared ? <span className={styles.sharedBadge}>{t('card.relation.shared')}</span> : null}
      </div>

      <div className={styles.cardName}>{project.name}</div>
      {caption === '' ? null : <div className={styles.cardCaption}>{caption}</div>}

      <div className={styles.cardSpacer} />

      <div className={styles.metaStrip}>
        <div className={styles.metaRow}>
          <span className={styles.metaSpacer} />
          <span className={styles.relation}>
            {shared ? t('card.relation.shared') : t('card.relation.owned')}
          </span>
        </div>
        <div className={styles.metaRow}>
          {/* Reserved: open reviews, overdue tasks and tags. `ProjectResponse` carries none of
              them, and the backend spec that adds them is the gap recorded in
              KLQ-20260819-003's Notes. The slot holds the row's height so the card does not
              change shape the day they arrive. */}
          <span className={styles.reserved} aria-hidden />
          <span className={styles.metaSpacer} />
          <span>{t('card.updated', { when: relativeTime(project.updated_at, locale) })}</span>
        </div>
      </div>
    </Link>
  );
}
